import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, Grid, useTheme, alpha } from "@mui/material";

interface Props {
  Icon: React.ElementType;
  statisticTitle: string;
  statisticFigure: number | null;
}

const getColor = (title: string): string => {
  switch (title) {
    case "Total books":
      return "#2563EB"; // refined modern blue
    case "Books completed":
      return "#10B981"; // emerald
    case "Books in Progress":
      return "#F59E0B"; // amber
    case "Books Delivered":
      return "#8B5CF6"; // purple / violet
    case "Monthly Progress":
      return "#EC4899"; // pink
    default:
      return "#3B82F6";
  }
};

export const StatisticsCard: React.FC<Props> = ({
  Icon,
  statisticFigure,
  statisticTitle,
}) => {
  const theme = useTheme();
  const value = statisticFigure ?? 0;
  const color = getColor(statisticTitle);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 900;
    const increment = value / (duration / 16) || value;

    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <Grid size={{ xs: 6, sm: 4, md: 2.4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.25,
          height: "100%",
          borderRadius: 3,
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(color, 0.08)
              : "#ffffff",
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? alpha(color, 0.25)
              : alpha(color, 0.16),
          position: "relative",
          overflow: "hidden",
          minWidth: 0,
          boxShadow:
            theme.palette.mode === "dark"
              ? "none"
              : "0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: `0 10px 24px -4px ${alpha(color, 0.18)}`,
            borderColor: alpha(color, 0.45),
            "& .metric-icon": {
              transform: "scale(1.08)",
              backgroundColor: alpha(color, 0.18),
            },
          },
        }}
      >
        {/* Subtle top accent bar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: color,
            opacity: 0.9,
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              fontSize: "0.78rem",
              letterSpacing: "0.01em",
              lineHeight: 1.2,
            }}
          >
            {statisticTitle}
          </Typography>

          <Box
            className="metric-icon"
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              backgroundColor: alpha(color, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: color,
              flexShrink: 0,
              transition: "all 0.3s ease",
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "text.primary",
            fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2.1rem" },
            lineHeight: 1.1,
            letterSpacing: "-0.03em",
          }}
        >
          {displayValue.toLocaleString()}
        </Typography>
      </Paper>
    </Grid>
  );
};